import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Plus, X, FileText } from "lucide-react";
import { Shell } from "@/components/Shell";
import { CASALE } from "@/data/questionnaire";
import { EVENT_TYPES, EVENT_SPACES, getEvent, upsertEvent, economicsBlocks } from "@/data/events";
import { EventCosts } from "@/components/EventCosts";
import { EventAiProposal } from "@/components/EventAiProposal";
import { EmailButton } from "@/components/EmailButton";
import { HandoffButton } from "@/components/HandoffButton";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
const inp = "w-full min-h-[48px] rounded-xl border border-[#E3D5C3] bg-white px-3 py-2 text-base text-[#2C221E] outline-none focus:border-[#C85A32] print:border-0 print:px-0 print:min-h-0";

const ListEditor = ({ items, onChange, render, add, testId }) => (
  <div className="grid gap-2" data-testid={testId}>
    {items.map((it, i) => (
      <div key={i} className="flex gap-2 items-start">
        {render(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))))}
        <button className="no-print w-10 h-12 grid place-items-center text-[#8A7970] hover:text-[#A83232]" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Rimuovi"><X size={16} /></button>
      </div>
    ))}
    <button className="no-print btn btn-ghost !min-h-[44px] text-sm justify-self-start" onClick={() => onChange([...items, add()])}><Plus size={16} /> Aggiungi riga</button>
  </div>
);

export default function EventSheetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(() => getEvent(id));
  if (!ev) {
    navigate("/eventi", { replace: true });
    return null;
  }
  const set = (patch) => setEv(upsertEvent({ ...ev, ...patch }));
  const setRole = (k, v) => set({ roles: { ...ev.roles, [k]: v } });
  const toggleSpace = (s) => set({ spaces: ev.spaces.includes(s) ? ev.spaces.filter((x) => x !== s) : [...ev.spaces, s] });
  const eventDoc = () => ({
    kind: "event", title: ev.title || "Scheda evento", subtitle: `${CASALE.name} · ${ev.date || "data da definire"} · ${ev.type} · ${ev.guests || 0} coperti · ${ev.spaces.join(", ")}`,
    blocks: [
      { heading: "Ruoli del giorno", rows: [["Responsabile sala", ev.roles.sala], ["Responsabile cucina", ev.roles.cucina], ["Extra sala", ev.roles.extraSala], ["Extra cucina", ev.roles.extraCucina], ["Referente cliente", ev.client]] },
      ...(ev.menu ? [{ heading: "Menù e note di cucina", paragraphs: [ev.menu] }] : []),
      { heading: "Timeline", rows: ev.timeline.map((r) => [r.time, r.what, r.who]) },
      { heading: "Briefing", bullets: ev.briefing },
      { heading: "Checklist", bullets: ev.checklist.map((c) => `${c.done ? "✓" : "☐"} ${c.text}`) },
      ...(ev.notes ? [{ heading: "Note", paragraphs: [ev.notes] }] : []),
    ],
  });

  return (
    <Shell wide header={
      <div className="flex gap-3 no-print">
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/eventi")} data-testid="event-back-btn"><ArrowLeft size={16} /> Schede</button>
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate(`/eventi/${id}/preventivo`)} data-testid="event-quote-btn"><FileText size={16} /> Preventivo</button>
        <EmailButton className="btn btn-ghost !min-h-[44px] text-sm" build={eventDoc} />
        <HandoffButton className="btn btn-ghost !min-h-[44px] text-sm" build={() => ({ ...eventDoc(), blocks: [...eventDoc().blocks, ...economicsBlocks(ev)] })} />
        <button className="btn btn-olive !min-h-[44px] text-sm" onClick={() => window.print()} data-testid="event-print-btn"><Printer size={16} /> Stampa / PDF</button>
      </div>
    }>
      <article className="plan fade-up card p-6 sm:p-10 grid gap-8" data-testid="event-sheet">
        <div className="border-b border-[#E3D5C3] pb-5">
          <p className="eyebrow">Scheda evento · {CASALE.name}</p>
          <input className="font-serif text-3xl sm:text-4xl font-bold text-[#2C221E] bg-transparent outline-none w-full mt-1 placeholder:text-[#CDBDA7]" placeholder="Nome dell'evento (es. Matrimonio Rossi–Bianchi)" value={ev.title} onChange={(e) => set({ title: e.target.value })} data-testid="event-title-input" />
        </div>

        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Data"><input type="date" className={inp} value={ev.date} onChange={(e) => set({ date: e.target.value })} data-testid="event-date-input" /></Field>
          <Field label="Tipo"><select className={inp} value={ev.type} onChange={(e) => set({ type: e.target.value })} data-testid="event-type-select">{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Coperti"><input type="number" inputMode="numeric" className={inp} value={ev.guests} onChange={(e) => set({ guests: e.target.value })} data-testid="event-guests-input" /></Field>
          <Field label="Referente cliente"><input className={inp} value={ev.client} onChange={(e) => set({ client: e.target.value })} placeholder="Nome e telefono" data-testid="event-client-input" /></Field>
        </div>

        <div>
          <p className="eyebrow mb-2">Spazi usati</p>
          <div className="flex flex-wrap gap-2">
            {EVENT_SPACES.map((s) => (
              <button key={s} className={`rounded-full px-4 min-h-[40px] text-sm border transition-colors ${ev.spaces.includes(s) ? "bg-[#2D4030] text-white border-[#2D4030]" : "bg-white border-[#E3D5C3] text-[#5C4D46] print:hidden"}`} onClick={() => toggleSpace(s)} data-testid={`event-space-${s}`}>{s}</button>
            ))}
          </div>
        </div>

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Ruoli del giorno</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Responsabile sala"><input className={inp} value={ev.roles.sala} onChange={(e) => setRole("sala", e.target.value)} data-testid="event-role-sala" /></Field>
            <Field label="Responsabile cucina"><input className={inp} value={ev.roles.cucina} onChange={(e) => setRole("cucina", e.target.value)} data-testid="event-role-cucina" /></Field>
            <Field label="Extra sala (nomi e orari)"><input className={inp} value={ev.roles.extraSala} onChange={(e) => setRole("extraSala", e.target.value)} /></Field>
            <Field label="Extra cucina (nomi e orari)"><input className={inp} value={ev.roles.extraCucina} onChange={(e) => setRole("extraCucina", e.target.value)} /></Field>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Menù e note di cucina</h2>
          <textarea className={`${inp} min-h-[90px]`} value={ev.menu} onChange={(e) => set({ menu: e.target.value })} placeholder="Portate, allergie, tavoli speciali, torta…" data-testid="event-menu-input" />
        </section>

        <EventCosts ev={ev} onChange={(costs) => set({ costs })} />

        <EventAiProposal ev={ev} onApply={(d) => set({
          timeline: Array.isArray(d.timeline) && d.timeline.length ? d.timeline.map((r) => ({ time: String(r.time || ""), what: String(r.what || ""), who: String(r.who || "") })) : ev.timeline,
          briefing: Array.isArray(d.briefing) && d.briefing.length ? d.briefing.map(String) : ev.briefing,
          checklist: Array.isArray(d.checklist) && d.checklist.length ? d.checklist.map((t) => ({ text: String(t), done: false })) : ev.checklist,
        })} />

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Timeline</h2>
          <div className="grid grid-cols-[80px_1fr_150px_auto] gap-2 text-xs uppercase tracking-widest text-[#8A7970] font-semibold px-1 mb-1"><span>Ora</span><span>Cosa</span><span>Chi</span><span /></div>
          <ListEditor items={ev.timeline} onChange={(timeline) => set({ timeline })} testId="event-timeline" add={() => ({ time: "", what: "", who: "" })}
            render={(r, upd) => (
              <div className="grid grid-cols-[80px_1fr_150px] gap-2 flex-1">
                <input className={inp} value={r.time} onChange={(e) => upd({ ...r, time: e.target.value })} />
                <textarea rows={1} className={`${inp} resize-none`} style={{ fieldSizing: "content" }} value={r.what} onChange={(e) => upd({ ...r, what: e.target.value })} />
                <input className={inp} value={r.who} onChange={(e) => upd({ ...r, who: e.target.value })} />
              </div>
            )} />
        </section>

        <div className="grid md:grid-cols-2 gap-8">
          <section>
            <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Briefing (10 minuti, tutti presenti)</h2>
            <ListEditor items={ev.briefing} onChange={(briefing) => set({ briefing })} testId="event-briefing" add={() => ""}
              render={(b, upd) => <input className={`${inp} flex-1`} value={b} onChange={(e) => upd(e.target.value)} />} />
          </section>
          <section>
            <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Checklist prima dell'evento</h2>
            <ListEditor items={ev.checklist} onChange={(checklist) => set({ checklist })} testId="event-checklist" add={() => ({ text: "", done: false })}
              render={(c, upd) => (
                <label className="flex items-center gap-3 flex-1 min-h-[48px]">
                  <input type="checkbox" className="w-6 h-6 accent-[#2D4030]" checked={c.done} onChange={(e) => upd({ ...c, done: e.target.checked })} />
                  <input className={`${inp} flex-1 ${c.done ? "line-through text-[#8A7970]" : ""}`} value={c.text} onChange={(e) => upd({ ...c, text: e.target.value })} />
                </label>
              )} />
          </section>
        </div>

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Note e imprevisti</h2>
          <textarea className={`${inp} min-h-[70px]`} value={ev.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="event-notes-input" />
        </section>
      </article>
    </Shell>
  );
}
